import localeCodes from "locale-codes";

const DEFAULT_LOCALE_OVERRIDES: Partial<Record<string, string>> = {
	pt: "pt-PT",
	ar: "ar-AE",
};

/**
 * Convert a language code (and optional country) into a full BCP 47 locale tag
 * suitable for API calls (e.g. "en" → "en-US", "el" → "el-GR").
 *
 * If the input is already a valid full locale (e.g. "en-US"), it is normalized and returned.
 */
export function calculateLocale(language: string, countryCode?: string): string {
	const input = (language || "").trim();
	if (!input) return "en-US";

	// 0) If input is already a full locale (e.g. "en-US"), normalize casing and return
	if (input.includes("-")) {
		const parts = input.split("-");
		if (parts.length === 2 && parts[0] && parts[1]) {
			const normalized = `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`;
			if (localeCodes.getByTag(normalized)) return normalized;
		}
	}

	const lang = input.toLowerCase();
	const cc = (countryCode || "").trim().toUpperCase();

	// 1) If language + country is provided and valid, always respect it
	if (lang && cc) {
		const candidate = `${lang}-${cc}`;
		if (localeCodes.getByTag(candidate)) return candidate;
	}

	// 2) Product defaults for languages where "likely subtags" are not what we want
	if (lang in DEFAULT_LOCALE_OVERRIDES) {
		const candidate = DEFAULT_LOCALE_OVERRIDES[lang];
		if (candidate && localeCodes.getByTag(candidate)) return candidate;
	}

	// 3) Try to infer the most likely region using Intl.Locale (CLDR likely subtags)
	if (typeof Intl !== "undefined" && "Locale" in Intl) {
		try {
			const maximized = new Intl.Locale(lang).maximize();
			const region = maximized?.region;

			if (region) {
				const candidate = `${lang}-${region}`;
				if (localeCodes.getByTag(candidate)) return candidate;
			}
		} catch {
			// Intl.Locale.maximize() is best-effort; fall back gracefully
		}
	}

	// 4) Last resort: return any valid locale for the given language
	if (lang) {
		const match = localeCodes.all.find((l) => l.tag.toLowerCase().startsWith(`${lang}-`));
		if (match?.tag) return match.tag;
	}

	// 5) Final fallback when the language is completely unknown
	return "en-US";
}
